import { BusinessActionEnum, BusinessProductAreaV2 } from './access-enums-v2';
/**
 * Payforms authorization on the Account Access V2 model (DEC-P2-03, revised).
 *
 * This file is the SINGLE definition of what a merchant user may do on the
 * `/api/business/payforms/**` routes and pages. The backend evaluates it
 * authoritatively against the effective grants (business ceiling INTERSECT
 * role grants); the frontend evaluates the same data only to hide or show
 * controls. Nothing here grants anything by itself: a capability is true only
 * when every required product area and action is present in the EFFECTIVE
 * grants, and effective grants can never exceed `businessAccess`.
 *
 * It lives next to the V2 enums (not under `src/public-experiences/`) because
 * the browser-safe import guard for that module freezes its allowlist; this
 * file imports only the enums and is equally browser-safe.
 */
export var PayformCapabilityEnum;
(function (PayformCapabilityEnum) {
    /** See the Payforms area at all (list forms, open detail). */
    PayformCapabilityEnum["VIEW"] = "VIEW";
    /** Create, edit, publish, disable forms. */
    PayformCapabilityEnum["MANAGE"] = "MANAGE";
    /** Submissions, customer directory, customer detail, payment history (PII). */
    PayformCapabilityEnum["PII"] = "PII";
    /** Cancel a customer's recurring payment. */
    PayformCapabilityEnum["CANCEL"] = "CANCEL";
    /**
     * Payment configuration on the connected account: read the payment-method
     * domain component and run "ensure payment domains" (DEC-P2-18). Requires
     * PAYFORM_MANAGE AND SETTINGS_MANAGE. Viewing a Payform (VIEW) or a
     * customer (PII) never grants it; PAYFORM_MANAGE alone never grants it.
     */
    PayformCapabilityEnum["PAYMENT_SETUP"] = "PAYMENT_SETUP";
})(PayformCapabilityEnum || (PayformCapabilityEnum = {}));
/**
 * Required grants per capability. Every capability implies VIEW (the
 * `PAYFORMS` area), so a role that cannot enter the area can do nothing in it.
 * Customer PII reuses the existing `CUSTOMERS` area and
 * `CUSTOMER_PROFILE_USE` action — there is deliberately no Payforms-specific
 * customer-details action.
 */
export const PAYFORM_CAPABILITIES = {
    [PayformCapabilityEnum.VIEW]: {
        productAreas: [BusinessProductAreaV2.PAYFORMS],
        actions: [],
    },
    [PayformCapabilityEnum.MANAGE]: {
        productAreas: [BusinessProductAreaV2.PAYFORMS],
        actions: [BusinessActionEnum.PAYFORM_MANAGE],
    },
    [PayformCapabilityEnum.PII]: {
        productAreas: [
            BusinessProductAreaV2.PAYFORMS,
            BusinessProductAreaV2.CUSTOMERS,
        ],
        actions: [BusinessActionEnum.CUSTOMER_PROFILE_USE],
    },
    [PayformCapabilityEnum.CANCEL]: {
        productAreas: [BusinessProductAreaV2.PAYFORMS],
        actions: [
            BusinessActionEnum.RECURRING_PAYMENT_CANCEL,
            BusinessActionEnum.CUSTOMER_PROFILE_USE,
        ],
    },
    [PayformCapabilityEnum.PAYMENT_SETUP]: {
        productAreas: [BusinessProductAreaV2.PAYFORMS],
        actions: [
            BusinessActionEnum.PAYFORM_MANAGE,
            BusinessActionEnum.SETTINGS_MANAGE,
        ],
    },
};
/**
 * Actions a business provisioned with Payforms should list in
 * `businessAccess.policy.sensitiveActions` (HYBRID mode: an active user must
 * identify with their PIN before the action is evaluated). There is no
 * policy builder in this package; provisioning code appends these when the
 * action is in the business ceiling.
 */
export const PAYFORMS_RECOMMENDED_SENSITIVE_ACTIONS = [
    BusinessActionEnum.RECURRING_PAYMENT_CANCEL,
];
/**
 * Intersects a business ceiling with a role's grants. Both inputs are the V2
 * grant buckets; the result can never contain a value absent from
 * `businessAccess`. Payment methods are not part of Payforms authorization.
 */
export const intersectPayformGrants = (businessAccess, roleGrants) => {
    const ceilingAreas = new Set(businessAccess.productAreas);
    const ceilingActions = new Set(businessAccess.actions);
    return {
        productAreas: roleGrants.productAreas.filter((area) => ceilingAreas.has(area)),
        actions: roleGrants.actions.filter((action) => ceilingActions.has(action)),
    };
};
const hasEvery = (granted, required) => required.every((value) => granted.includes(value));
/**
 * Evaluates the five Payforms capabilities against EFFECTIVE grants (already
 * intersected with the business ceiling — use `intersectPayformGrants`).
 * Pure and total: unknown values are ignored, nothing is inferred.
 */
export const evaluatePayformCapabilities = (effectiveGrants) => {
    const evaluate = (capability) => {
        const requirement = PAYFORM_CAPABILITIES[capability];
        return (hasEvery(effectiveGrants.productAreas, requirement.productAreas) &&
            hasEvery(effectiveGrants.actions, requirement.actions));
    };
    return {
        [PayformCapabilityEnum.VIEW]: evaluate(PayformCapabilityEnum.VIEW),
        [PayformCapabilityEnum.MANAGE]: evaluate(PayformCapabilityEnum.MANAGE),
        [PayformCapabilityEnum.PII]: evaluate(PayformCapabilityEnum.PII),
        [PayformCapabilityEnum.CANCEL]: evaluate(PayformCapabilityEnum.CANCEL),
        [PayformCapabilityEnum.PAYMENT_SETUP]: evaluate(PayformCapabilityEnum.PAYMENT_SETUP),
    };
};
